import { useMutation, useQueryClient, useQuery } from "react-query";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import store from "store";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import {
  updateProfile,
  changePassword,
  singleLogin,
  register,
  requestRecovery,
  verifyToken,
  reset,
  changeLanguage,
  verifyGoogleToken,
  logout,
  revokeLogin,
  loginWithSso,
  generateOtp,
} from "apis/user";
import { App } from "antd";
import {
  setConfig,
  setApp,
  logout as reduxLogout,
  setMenu,
  setOption,
  setPermissions,
} from "redux/configSlice";
import { setReference } from "redux/referenceSlice";
import { resetLoginState } from "redux/loginSlice";
import { useConfigSelector, useLivestreamSelector } from "hooks/useSelector";
import { setToken, setSavedEmail, getToken, removeToken } from "utils/cookies";
import { findHrCompanies, generateBaseUri } from "utils/helper";
import Axios from "utils/axios";
import useRefetchQueries from "hooks/useRefetchQueries";

export const useUpdateProfile = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => updateProfile(payload), {
    onSuccess: (response) => {
      if (response.data.success) {
        const data = response.data.data || {};
        message.success(
          data?.otp_required ? data?.message : response.data?.message
        );
      } else {
        message.error(response.data?.message);
      }
    },
    onError: (_err, tax, context) => {
      message.error(t("error.failed_save_data_to_server"));
    },
  });
};

export const useRevokeLogin = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation(() => revokeLogin(), {
    onSuccess: (response) => {
      if (response.data.success) {
        message.success(response.data?.message);
      } else {
        message.error(response.data?.message);
      }
    },
    onError: (error) =>
      message.error(t("'profile.cannot_logout_from_all_device'")),
  });
};

export const useChangePassword = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => changePassword(payload), {
    onSuccess: (response) => {
      if (response.data.success) {
        message.success(response.data?.message);
      } else {
        message.error(response.data?.message);
      }
    },
    onError: (error) => message.error(t("error.failed_save_data_to_server")),
  });
};

export const useLogout = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { apiToken } = useConfigSelector();
  const payload = apiToken ? apiToken : getToken() ? getToken() : null;
  return useMutation(() => logout(payload), {
    onMutate: () => {
      // Dihapus dulu previous path dan selected endpoinnya
      store.remove("app.endpoint");
      store.remove("app.previousPathname");
      store.remove("app.role");
      Axios.defaults.baseURL = generateBaseUri();
      // Reset reduxnya
      dispatch(reduxLogout());
      navigate("/user/login", { replace: true });
    },
    onSuccess: (response) => {
      if (!response.data.success) {
        message.error(response.data.message);
      }
    },
    onSettled: () => {
      // Dihapus tokennya
      removeToken();
    },
  });
};

const defaultCustomOptions = {
  withSetEndpoint: false,
  withSetEmail: true,
};

export const useSingleLogin = (customOptions = {}) => {
  customOptions = { ...defaultCustomOptions, ...customOptions };

  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation(
    ({ email, password, is_otp = 0 }) =>
      singleLogin(email, password, 1, is_otp),
    {
      onSuccess: (response) => {
        if (response?.data.success) {
          const data = response?.data?.data || {};
          if (!data?.otp_required) {
            successLogin(data, dispatch, customOptions, queryClient);
          }
        }
      },
    }
  );
};

export const useVerifyGoogleToken = (customOptions = {}) => {
  customOptions = { ...defaultCustomOptions, ...customOptions };
  const { t } = useTranslation();
  const { message } = App.useApp();

  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation(({ code }) => verifyGoogleToken(code), {
    onSuccess: (response) => {
      if (response?.data.success) {
        successLogin(response.data?.data, dispatch, customOptions, queryClient);
      } else {
        message.error(response.data?.message || t("error.an_error_happened"));
      }
    },
    onError: (e) => {
      message.error(e.data?.message || t("error.an_error_happened"));
    },
  });
};

// Callback untuk login sukses
export const successLogin = (
  { init, data, companies, user },
  dispatch,
  customOptions = {},
  queryClient
) => {
  customOptions = { ...defaultCustomOptions, ...customOptions };
  const config = {
    apiToken: data?.access_token,
    expiredToken: data?.expires_at,
    // Jika single company (ada init) diset dari init, jika tidak diambil di user
    locale: init ? init?.user?.lang : user?.lang || "id-ID",
  };
  setToken(data.access_token);
  if (customOptions.withSetEmail) {
    setSavedEmail(user.email);
  }
  const hrCompanies = findHrCompanies(companies);
  // Set email
  if (hrCompanies.length === 1) {
    // Find selected company object
    const findCompany = companies.find(
      (row) => row.website_id.toString() === init.website_id.toString()
    );
    if (findCompany) {
      config.endpoint = findCompany?.endpoint;
      config.selectedCompany = findCompany;
      if (customOptions.withSetEndpoint) {
        store.set("app.endpoint", config.endpoint);
        Axios.defaults.baseURL = generateBaseUri();
        queryClient.clear();
      }
    }
  } else {
    store.remove("app.endpoint");
  }

  let app = { user, companies };
  if (init) {
    if (init?.setting) {
      app.setting = init.setting;
    }
    if (init?.user) {
      app.user = init.user;
    }
    if (init?.option) {
      dispatch(setOption(init.option));
    }
    if (init?.reference) {
      dispatch(setReference(init.reference));
    }

    if (init?.user?.permissions) {
      dispatch(setPermissions(init?.user?.permissions));
    }

    if (init?.livestream) {
      const livestream = init.livestream;
      livestream.is_show_thumbnail = true;
      app.livestream = livestream;
    }

    //set config
    if (!_.isEmpty(init?.user?.app_roles)) {
      const roles = init?.user?.app_roles || ["admin"];
      const findRole = roles.includes("admin") ? "admin" : roles?.[0];
      config.role = findRole;
      store.set("app.role", findRole);
    }

    app = {
      ...app,
      lang: init.lang,
      billing: init.billing,
      translation: init.translation,
      website_id: parseInt(init.website_id, 10),
      setting: init.setting,
      dashboard: init.dashboard,
      loadedInit: true,
      gajihubStartDate: init?.gajihub_start_date,
      employeeCountAvailable: init?.employee_count_available || [],
      defaultMinEmployeeOrder: init?.default_min_employee_order || 0,
      maxCompanies: init?.max_companies || 0,
      showNps: init?.show_nps || 0,
      showQueueCount: init?.show_hr_queue_count || 0,
      announcement: init?.announcement || "",
    };
  }
  // Jika belum punya perusahaan
  if (hrCompanies.length <= 1) {
    app.loadedInit = true;
  } else {
    if (!customOptions.withSetEndpoint) {
      app.loadedInit = false;
    }
  }
  // Set config untuk di simpan di redux
  dispatch(setConfig(config));
  dispatch(setApp(app));
  if (init?.user?.menus) {
    dispatch(setMenu(init.user?.menus));
  }
  dispatch(resetLoginState());
};

export const useRegister = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => register(payload), {
    onError: (_err) => {
      message.error(
        !_.isEmpty(_err) && _.isString(_err)
          ? _err
          : t("error.an_error_happened")
      );
    },
  });
};

export const useRequestRecovery = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useMutation((payload) => requestRecovery(payload), {
    onError: (_err) => {
      message.error(
        !_.isEmpty(_err) && _.isString(_err)
          ? _err
          : t("error.an_error_happened")
      );
    },
  });
};

export function useVerifyToken(payload = {}, options = {}) {
  const { t } = useTranslation();
  const { message } = App.useApp();

  return useQuery(
    ["verifyToken/resetPassword"],
    async () => {
      let response;
      try {
        const {
          data: { data },
        } = await verifyToken(payload.token);
        response = data;
      } catch (error) {
        throw new Error("Failed to load data from server!");
      }
      return response;
    },
    {
      onError: (error) =>
        message.error(error.message || t("error.an_error_happened")),
      ...options,
    }
  );
}

export const useResetPassword = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  return useMutation((payload) => reset(payload), {
    onSuccess: (response) => {
      if (response.data.success) {
        successLogin(response.data?.data, dispatch, {}, queryClient);
      }
    },
    onError: (_err) => {
      message.error(
        !_.isEmpty(_err) && _.isString(_err)
          ? _err
          : t("error.an_error_happened")
      );
    },
  });
};

export const useChangeLanguage = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const { refetchQueries } = useRefetchQueries();
  const config = useConfigSelector();
  const livestream = useLivestreamSelector();
  const dispatch = useDispatch();
  return useMutation(({ locale }) => changeLanguage(locale), {
    onMutate: ({ locale }) => {
      message.open({
        type: "loading",
        content: t("topBar.loading_language"),
        duration: 0,
      });
      const prevLocale = _.cloneDeep(config?.locale);

      store.set("app.settings.locale", locale);
      dispatch(setConfig({ locale }));

      return { locale: prevLocale };
    },
    onSuccess: async (response, { locale }) => {
      if (response?.data.success) {
        message.destroy();
        store.set("app.settings.locale", locale);
        dispatch(setConfig({ locale }));
        dispatch(
          setApp({
            ...response.data?.data?.init,
            website_id: parseInt(response.data?.data?.init?.website_id, 10),
            livestream: {
              ...livestream,
              ...response.data?.data?.init?.livestream,
            },
          })
        );
        dispatch(setMenu({ ...response.data?.data?.init?.user?.menus }));
        dispatch(setReference({ ...response.data?.data?.init?.reference }));
        message.success(response.data?.message);

        await refetchQueries();
      }
    },
    onError: (_err, payload, context) => {
      message.destroy();
      message.error(
        !_.isEmpty(_err) && _.isString(_err)
          ? _err
          : t("error.an_error_happened")
      );
      store.set("app.settings.locale", context?.locale);
      dispatch(setConfig({ locale: context?.locale }));
    },
  });
};

export const useLoginWithSsoMutation = () => {
  return useMutation(loginWithSso);
};

export const useGenerateOtp = () => {
  return useMutation(generateOtp);
};
